// src/padplanner/TemplatesTab.jsx
//
// The plan page's Templates tab: mockup 4's left panel (search, set chips,
// grouped sections, a three-column tile grid), browse-only in S3.
//
// WHAT IT SHOWS. What the plan can use, by the editor's own rule (D-S4-1,
// editorPalette.js paletteSets): the set pinned to it, if any, then the
// published AI PRIME standard set, each named with its version. An unpinned
// plan is not a plan without a library: it uses the standard set, and when
// its organization has published one of its own, it can pin that once
// (ChooseLibrary.jsx; the pin is final). A pinned set retired since is still
// shown, marked Retired: published content never changes, so it is what the
// plan was built with. Only when there is no set at all (no pin and no
// published standard set, which 152's index makes a broken deploy) does it
// say "No template library yet".
//
// CUT UNTIL S4. Dragging onto the map, the Selected panel (rotation, count,
// position, service company) and snapping. The mockup's footer ("Drag onto the
// map · true-scale footprints · snap to pad edges") says what S4 will do; the
// footer here says what S3 does not.
//
// MANAGE LIBRARY. Shown to a library admin only, as the server says
// (can_manage on the list it already reads).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { SectionLabel } from "./SidePanel";
import { getTemplateSet, listTemplateSets } from "../api/padplannerApi";
import ReadFailed from "./ReadFailed";
import ChooseLibrary from "./ChooseLibrary";
import { paletteSets } from "./editorPalette";
import {
  CATEGORY_LABELS, NO_LIBRARY_TEXT, NO_SET_PUBLISHED_TEXT, NO_SOURCE_TEXT, PLACING_LATER_TEXT, PREVIEW_COLORS,
  feetText, footprintPath, groupTemplates, matchesSearch, setChipLabel, templateDims,
} from "./templateVocabulary";

const field = "rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";
const chipClass = (on) =>
  `rounded-full border px-2.5 py-1 text-[12px] ${on ? "border-accent-action bg-accent-action/15 text-fg-primary" : "border-bg-border text-fg-secondary hover:text-fg-primary"}`;

export default function TemplatesTab({ plan, headerAction = null, canEdit = false, onPinned = null }) {
  const navigate = useNavigate();
  // By id: every save hands the page a new plan object, and that is not a new pin.
  const pinnedId = plan.template_set?.id ?? null;
  // listTemplateSets(), once per pin; null while loading.
  const [list, setList] = useState(null);
  // {setId: {set, templates}} as loaded.
  const [sets, setSets] = useState({});
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = () => { setFailed(false); setAttempt((n) => n + 1); };
  const [chosenId, setChosenId] = useState(null);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listTemplateSets()
      .then((l) => { if (!cancelled) setList(l); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [pinnedId, attempt]);

  const { chips, choosable, canChoose } = useMemo(() => paletteSets(plan, list?.sets ?? []), [plan, list]);
  const active = chips.find((c) => c.id === chosenId) ?? chips[0] ?? null;
  const activeId = active?.id ?? null;

  useEffect(() => {
    if (!activeId || sets[activeId]) return undefined;
    let cancelled = false;
    getTemplateSet(activeId)
      .then((one) => { if (!cancelled) setSets((x) => ({ ...x, [activeId]: one })); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [activeId, sets, attempt]);

  const loaded = activeId ? sets[activeId] : null;
  const shown = useMemo(() => (loaded?.templates ?? []).filter((t) => matchesSearch(t, q)), [loaded, q]);
  const groups = useMemo(() => groupTemplates(shown), [shown]);
  const selected = shown.find((t) => t.id === selectedId) ?? null;
  const manage = list?.can_manage === true && (
    <button type="button" className="op-btn op-btn--sm self-start" data-testid="pp-manage-library"
      onClick={() => navigate({ to: "/padplanner/library" })}>Manage library</button>
  );

  // No set at all: only once the list says so, never while it loads or after it failed.
  if (list && !failed && chips.length === 0) {
    return (
      <div className="flex flex-col gap-3" data-testid="pp-templates-empty">
        <p className="text-[13px] text-fg-primary">{NO_LIBRARY_TEXT}</p>
        <p className="op-meta">{NO_SET_PUBLISHED_TEXT}</p>
        {headerAction}
        {manage}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pp-templates">
      {headerAction}
      {canChoose && list && (
        <ChooseLibrary plan={plan} choosable={choosable} canPin={canEdit} onPinned={(p, id) => { setChosenId(id); onPinned?.({ plan: p }); }} />
      )}
      {manage}
      <input type="search" className={field} placeholder="Search templates…" aria-label="Search templates"
        value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Template sets">
        {chips.map((c) => (
          <button key={c.id} type="button" aria-pressed={activeId === c.id} className={chipClass(activeId === c.id)}
            data-testid="pp-set-chip" data-standard={String(c.standard)}
            onClick={() => { setChosenId(c.id); setSelectedId(null); }}>
            {setChipLabel(c)}{c.retired && <span className="ml-1.5 op-chip px-1 py-0" data-testid="pp-set-retired">Retired</span>}
          </button>
        ))}
      </div>

      {failed && <ReadFailed what="the template library" onRetry={retry} testid="pp-templates-failed" />}
      {!failed && !loaded && <p className="op-meta">Loading templates…</p>}
      {loaded && shown.length === 0 && (
        <p className="op-meta">{q.trim() ? `No templates match “${q.trim()}”.` : "This set has no templates."}</p>
      )}

      {selected && <TemplateDetail t={selected} onClose={() => setSelectedId(null)} />}

      {groups.map((g) => (
        <section key={g.key} className="flex flex-col gap-1.5" aria-labelledby={`pp-tg-${g.key}`}>
          <div className="flex items-center justify-between">
            <SectionLabel id={`pp-tg-${g.key}`}>{g.label}</SectionLabel>
            <span className="op-mono text-[11px] text-fg-secondary" data-testid="pp-group-count">{g.templates.length}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {g.templates.map((t) => (
              <button key={t.id} type="button" aria-pressed={selectedId === t.id} data-testid="pp-template-tile"
                onClick={() => setSelectedId((cur) => (cur === t.id ? null : t.id))}
                className={`flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded border px-1.5 py-2 text-center text-[11.5px] leading-tight ${selectedId === t.id ? "border-accent-action bg-accent-action/10 text-fg-primary" : "border-bg-border bg-bg text-fg-primary hover:border-fg-secondary"}`}>
                <TemplatePreview t={t} />
                <span>{t.name}</span>
              </button>
            ))}
          </div>
        </section>
      ))}

      <p className="op-meta border-t border-bg-border pt-2">{PLACING_LATER_TEXT}</p>
    </div>
  );
}

/** The template's own shape, fitted to a tile. */
export function TemplatePreview({ t, w = 44, h = 28 }) {
  if (t.kind === "footprint") {
    const d = footprintPath(t.footprint_ft, { w, h });
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" data-testid="pp-preview-footprint">
        {d && <path d={d} fill={PREVIEW_COLORS.footprint} fillOpacity="0.8" stroke={PREVIEW_COLORS.footprint} strokeWidth="1" />}
      </svg>
    );
  }
  if (t.kind === "zone") {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" data-testid="pp-preview-zone">
        <circle cx={w / 2} cy={h / 2} r={h / 2 - 2} fill="none" stroke={PREVIEW_COLORS.zone} strokeWidth="1.5" strokeDasharray="3 2" />
      </svg>
    );
  }
  if (t.kind === "line") {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" data-testid="pp-preview-line">
        <line x1="3" y1={h / 2} x2={w - 3} y2={h / 2} stroke={PREVIEW_COLORS.line} strokeWidth="2.5" />
      </svg>
    );
  }
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" data-testid="pp-preview-pattern">
      {[0.2, 0.4, 0.6, 0.8].map((f) => <circle key={f} cx={w * f} cy={h / 2} r="3" className="fill-fg-primary" />)}
    </svg>
  );
}

function TemplateDetail({ t, onClose }) {
  const rows = [
    ...(t.kind === "footprint" ? [["Length", feetText(t.length_ft)], ["Width", feetText(t.width_ft)]] : []),
    ...(t.kind === "zone" ? [["Radius", feetText(t.radius_ft)]] : []),
    ["Category", CATEGORY_LABELS[t.category] ?? t.category],
    ["Layer", t.default_layer],
    ["Source", t.dims_source ?? NO_SOURCE_TEXT],
  ];
  return (
    <div className="flex flex-col gap-2 rounded border border-bg-border bg-bg px-3 py-2.5" data-testid="pp-template-detail">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[13px] font-semibold text-fg-primary">{t.name}</p>
          <p className="op-meta">{templateDims(t)}</p>
        </div>
        <button type="button" className="op-meta hover:text-fg-primary" aria-label="Close template details" onClick={onClose}>Close</button>
      </div>
      <dl className="grid grid-cols-[72px_1fr] gap-x-3 gap-y-1 text-[12.5px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="op-label uppercase">{k}</dt>
            <dd className="text-fg-primary">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
